import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { parkingApi, usePkList, usePkMutation, type PkSpace, type SpaceStatus } from "../api/parkingApi";
import { ConsolePage, DetailRow, Drawer, FilterSelect, KpiRow, KpiTile, LoadState, SearchBox, SiteTree, ToolButton, type TreeGroup } from "../components/console/Console";
import FormModal, { type FormValues } from "../components/console/FormModal";
import { LOT_ICON, PARKING_BASE, PlateBadge, SPACE_DOT, SPACE_STYLE, SPACE_TYPE_ICON, errorText } from "./parkingUi";
import { buildTicketPdf, openPdf } from "./parkingPdf";

// ===========================================================================
// Control de estacionamientos → Ocupación: mapa de cajones por nivel (cuadrícula
// con colores), árbol de estacionamientos y niveles, detalle del cajón y
// entrada manual (abre el ticket e imprime su comprobante con QR).
// ===========================================================================

const STATUSES: SpaceStatus[] = ["free", "occupied", "reserved", "pension", "out_of_service"];

export default function OccupancyScreen() {
  const { t, i18n } = useTranslation("parking");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const lotsQ = usePkList("lots");
  const lots = useMemo(() => lotsQ.data ?? [], [lotsQ.data]);
  const lotId = params.get("lot") || lots[0]?.id || "";
  const [level, setLevel] = useState<string>("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PkSpace | null>(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const spacesQ = usePkList("spaces", lotId ? { lot_id: lotId } : undefined, { enabled: Boolean(lotId), refetchInterval: 20_000 });
  const spaces = useMemo(() => spacesQ.data ?? [], [spacesQ.data]);
  const lot = lots.find((l) => l.id === lotId);

  const setSpace = usePkMutation(({ id, status: st }: { id: string; status: SpaceStatus }) => parkingApi.update("spaces", id, { status: st }));
  const entry = usePkMutation(parkingApi.entry);

  const tree: TreeGroup[] = lots.map((l) => ({
    id: l.id,
    label: l.name,
    icon: LOT_ICON[l.type] ?? "ri-parking-box-line",
    items: (l.levels ?? []).map((lv) => ({ id: `${l.id}::${lv.id}`, label: `${lv.id} · ${lv.name}`, icon: "ri-stack-line" })),
  }));

  const counts = Object.fromEntries(STATUSES.map((s) => [s, spaces.filter((x) => x.status === s).length])) as Record<SpaceStatus, number>;
  const usable = spaces.length - counts.out_of_service;
  const pct = usable ? Math.round(((counts.occupied + counts.pension + counts.reserved) / usable) * 100) : 0;
  const levels = (lot?.levels ?? []).filter((lv) => !level || lv.id === level);
  const q = search.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "");
  const visible = (s: PkSpace) => (!status || s.status === status) && (!q || s.code.toUpperCase().includes(q) || String(s.plate || "").toUpperCase().replace(/[^A-Z0-9-]/g, "").includes(q));

  const changeStatus = async (st: SpaceStatus) => {
    if (!selected) return;
    try {
      await setSpace.mutateAsync({ id: selected.id, status: st });
      toast.success(t("occupancy.statusChanged"));
      setSelected(null);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const submitEntry = async (v: FormValues) => {
    try {
      const out = await entry.mutateAsync({ lot_id: lotId, plate: String(v.plate || "").trim() || undefined, vehicle_type: String(v.vehicle_type || "car") });
      if (out.pension) {
        toast.success(t("occupancy.pensionPass", { interpolation: { escapeValue: false }, name: out.pension.holder_name }));
      } else if (out.ticket) {
        toast.success(t("occupancy.ticketOpened", { interpolation: { escapeValue: false }, folio: out.ticket.folio }));
        openPdf(await buildTicketPdf({ ticket: out.ticket, t, lang: i18n.language }), `${out.ticket.folio}.pdf`);
      }
      setEntryOpen(false);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  return (
    <ConsolePage
      icon="ri-layout-grid-line"
      title={t("occupancy.title")}
      subtitle={lot ? `${lot.name} · ${lot.opening_hours ?? ""}` : t("occupancy.subtitle")}
      actions={
        <>
          <ToolButton icon="ri-refresh-line" onClick={() => spacesQ.refetch()}>{t("common.refresh")}</ToolButton>
          <ToolButton icon="ri-login-box-line" variant="primary" onClick={() => setEntryOpen(true)} disabled={!lotId}>{t("occupancy.manualEntry")}</ToolButton>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        <SiteTree
          title={t("occupancy.lots")}
          className="rounded-xl border lg:w-64 lg:shrink-0"
          groups={tree}
          selectedGroup={lotId}
          selectedItem={level ? `${lotId}::${level}` : null}
          onSelectGroup={(id) => {
            if (id) setParams({ lot: id });
            setLevel("");
          }}
          onSelectItem={(id) => {
            const [l, lv] = id.split("::");
            setParams({ lot: l });
            setLevel(lv);
          }}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <KpiRow>
            <KpiTile icon="ri-pie-chart-2-line" tone={pct >= 90 ? "error" : pct >= 70 ? "warning" : "success"} label={t("kpi.occupancy")} value={`${pct} %`} hint={t("kpi.freeOf", { interpolation: { escapeValue: false }, free: counts.free, total: usable })} />
            <KpiTile icon="ri-checkbox-blank-circle-line" tone="success" label={t("spaceStatus.free")} value={counts.free} />
            <KpiTile icon="ri-car-line" tone="error" label={t("spaceStatus.occupied")} value={counts.occupied} />
            <KpiTile icon="ri-bookmark-line" tone="warning" label={t("spaceStatus.reserved")} value={counts.reserved} />
            <KpiTile icon="ri-vip-crown-line" tone="info" label={t("spaceStatus.pension")} value={counts.pension} />
            <KpiTile icon="ri-tools-line" tone="neutral" label={t("spaceStatus.out_of_service")} value={counts.out_of_service} />
          </KpiRow>
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={search} onChange={setSearch} placeholder={t("occupancy.searchPlaceholder")} />
            <FilterSelect value={status} onChange={setStatus} label={t("common.status")} options={[{ value: "", label: t("common.allStatuses") }, ...STATUSES.map((s) => ({ value: s, label: t(`spaceStatus.${s}`) }))]} />
            <div className="ml-auto flex flex-wrap items-center gap-3 text-[11px] text-content-secondary">
              {STATUSES.map((s) => (
                <span key={s} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-sm ${SPACE_DOT[s]}`} />{t(`spaceStatus.${s}`)}</span>
              ))}
            </div>
          </div>
          <LoadState loading={lotsQ.isLoading || spacesQ.isLoading} error={lotsQ.error || spacesQ.error} empty={!spaces.length} emptyText={t("occupancy.empty")} onRetry={() => spacesQ.refetch()}>
            <div className="space-y-4">
              {levels.map((lv) => {
                const mine = spaces.filter((s) => s.level === lv.id);
                return (
                  <section key={lv.id} className="rounded-xl border border-line bg-card">
                    <header className="flex items-center justify-between border-b border-line px-3 py-2">
                      <h2 className="text-sm font-semibold text-content"><i className="ri-stack-line text-content-muted" /> {lv.id} · {lv.name}</h2>
                      <span className="text-xs text-content-secondary">{t("occupancy.levelSummary", { interpolation: { escapeValue: false }, free: mine.filter((s) => s.status === "free").length, total: mine.length })}</span>
                    </header>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(64px,1fr))] gap-1.5 p-3">
                      {mine.map((s) => {
                        const dim = !visible(s);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setSelected(s)}
                            title={`${s.code} · ${t(`spaceStatus.${s.status}`)}${s.plate ? ` · ${s.plate}` : ""}`}
                            className={`relative flex h-14 flex-col items-center justify-center rounded-md border text-[11px] font-semibold transition ${SPACE_STYLE[s.status]} ${dim ? "opacity-25" : "hover:ring-2 hover:ring-primary-500/40"}`}
                          >
                            {SPACE_TYPE_ICON[s.type] && <i className={`${SPACE_TYPE_ICON[s.type]} absolute right-1 top-0.5 text-[10px] opacity-80`} />}
                            <span>{s.code}</span>
                            {s.plate && <span className="max-w-full truncate px-0.5 font-mono text-[9px] font-normal opacity-90">{s.plate}</span>}
                            {s.status === "occupied" && !s.plate && <i className="ri-car-fill text-sm" />}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </LoadState>
        </div>
      </div>

      <Drawer
        open={Boolean(selected)}
        title={selected ? t("occupancy.spaceTitle", { interpolation: { escapeValue: false }, code: selected.code }) : ""}
        subtitle={lot?.name}
        onClose={() => setSelected(null)}
        footer={
          selected && (
            <>
              {selected.ticket_id && <ToolButton icon="ri-cash-line" variant="primary" onClick={() => navigate(`${PARKING_BASE}/cobro?ticket=${encodeURIComponent(selected.ticket_id!)}`)}>{t("occupancy.charge")}</ToolButton>}
              {!selected.ticket_id && selected.status !== "reserved" && selected.status !== "pension" && <ToolButton icon="ri-bookmark-line" onClick={() => changeStatus("reserved")}>{t("occupancy.reserve")}</ToolButton>}
              {!selected.ticket_id && selected.status !== "free" && <ToolButton icon="ri-checkbox-circle-line" onClick={() => changeStatus("free")}>{t("occupancy.release")}</ToolButton>}
              {!selected.ticket_id && selected.status !== "out_of_service" && <ToolButton icon="ri-tools-line" variant="danger" onClick={() => changeStatus("out_of_service")}>{t("occupancy.outOfService")}</ToolButton>}
            </>
          )
        }
      >
        {selected && (
          <div>
            <div className={`mb-4 flex h-24 items-center justify-center rounded-xl border text-3xl font-bold ${SPACE_STYLE[selected.status]}`}>{selected.code}</div>
            <DetailRow label={t("common.status")}>{t(`spaceStatus.${selected.status}`)}</DetailRow>
            <DetailRow label={t("occupancy.level")}>{selected.level}</DetailRow>
            <DetailRow label={t("occupancy.type")}>{t(`spaceType.${selected.type}`)}</DetailRow>
            <DetailRow label={t("common.plate")}><PlateBadge plate={selected.plate} /></DetailRow>
            {selected.pension_id && (
              <DetailRow label={t("occupancy.pension")}>
                <button type="button" className="text-primary-500 hover:underline" onClick={() => navigate(`${PARKING_BASE}/pensiones`)}>{t("occupancy.viewPension")}</button>
              </DetailRow>
            )}
            {selected.ticket_id && (
              <DetailRow label={t("occupancy.ticket")}>
                <button type="button" className="text-primary-500 hover:underline" onClick={() => navigate(`${PARKING_BASE}/tickets?ticket=${encodeURIComponent(selected.ticket_id!)}`)}>{t("occupancy.viewTicket")}</button>
              </DetailRow>
            )}
            {selected.notes && <p className="mt-3 rounded-lg bg-subtle p-2 text-xs text-content-secondary">{selected.notes}</p>}
          </div>
        )}
      </Drawer>

      {entryOpen && (
        <FormModal
          title={t("occupancy.manualEntry")}
          submitLabel={t("occupancy.openTicket")}
          busy={entry.isPending}
          onClose={() => setEntryOpen(false)}
          onSubmit={submitEntry}
          initial={{ vehicle_type: "car" }}
          fields={[
            { key: "plate", label: t("common.plate"), placeholder: "ABC-123-D", hint: t("occupancy.plateHint") },
            { key: "vehicle_type", label: t("occupancy.vehicleType"), type: "select", required: true, options: (["car", "motorcycle", "truck"] as const).map((v) => ({ value: v, label: t(`vehicle.${v}`) })) },
          ]}
        />
      )}
    </ConsolePage>
  );
}
